import React, { useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link } from "react-router-dom";
import { 
  Card, 
  Accordion, 
  Container, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Badge,
  Alert,
  ButtonGroup
} from "react-bootstrap";
import {
  QuestionCircle,
  Search,
  FileText,
  People,
  Shield,
  Key,
  Building,
  Calculator,
  CreditCard,
  ClockHistory,
  ExclamationTriangle,
  CheckCircle,
  ArrowRight,
  Book,
  XCircle,
  Phone,
  LifePreserver,
  Envelope,
  Filter
} from "react-bootstrap-icons";

export default function FAQ() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  // Catégories de FAQ
  const categories = [
    { id: "all", name: "Toutes", icon: <QuestionCircle />, count: 24 },
    { id: "account", name: "Compte", icon: <People />, count: 6 },
    { id: "security", name: "Sécurité", icon: <Shield />, count: 5 },
    { id: "taxes", name: "Taxes", icon: <Calculator />, count: 7 },
    { id: "payment", name: "Paiements", icon: <CreditCard />, count: 4 },
    { id: "technical", name: "Technique", icon: <ExclamationTriangle />, count: 3 },
  ];

  // Questions et réponses
  const faqItems = [
    // Compte & Connexion
    {
      id: 1,
      category: "account",
      question: "Comment créer un compte sur la plateforme ?",
      answer: "Pour créer un compte : 1. Rendez-vous sur la page d'inscription 2. Remplissez le formulaire avec vos informations personnelles 3. Vérifiez votre email pour confirmer l'inscription 4. Connectez-vous avec vos identifiants",
      tags: ["compte", "inscription", "démarrage"]
    },
    {
      id: 2,
      category: "account",
      question: "J'ai oublié mon mot de passe, que faire ?",
      answer: "Cliquez sur 'Mot de passe oublié' sur la page de connexion. Entrez votre adresse email et suivez les instructions pour réinitialiser votre mot de passe. Un lien temporaire vous sera envoyé par email.",
      tags: ["mot de passe", "connexion", "sécurité"]
    },
    {
      id: 3,
      category: "account",
      question: "Comment modifier mes informations personnelles ?",
      answer: "Accédez à votre profil via le menu déroulant en haut à droite. Cliquez sur 'Modifier le profil' pour mettre à jour vos informations. N'oubliez pas de sauvegarder les modifications.",
      tags: ["profil", "modification", "informations"]
    },
    {
      id: 4,
      category: "account",
      question: "Comment changer mon adresse email ?",
      answer: "Dans votre profil, modifiez votre adresse email dans la section 'Informations de contact'. Un email de confirmation sera envoyé à votre nouvelle adresse pour valider le changement.",
      tags: ["email", "contact", "modification"]
    },
    {
      id: 5,
      category: "account",
      question: "Mon compte est bloqué, que dois-je faire ?",
      answer: "Contactez le support technique par email à support@taxes.gov.sn ou appelez le 33 889 10 20. Préparez votre numéro d'identifiant fiscal pour accélérer le processus.",
      tags: ["blocage", "support", "assistance"]
    },
    {
      id: 6,
      category: "account",
      question: "Comment ajouter un utilisateur supplémentaire à mon compte ?",
      answer: "Les utilisateurs supplémentaires peuvent être ajoutés par l'administrateur principal du compte via la section 'Gestion des utilisateurs'. Des permissions spécifiques peuvent être attribuées à chaque utilisateur.",
      tags: ["utilisateurs", "gestion", "permissions"]
    },
    
    // Sécurité & Confidentialité
    {
      id: 7,
      category: "security",
      question: "Mes données sont-elles sécurisées ?",
      answer: "Oui, nous utilisons le chiffrement SSL 256-bit et conformité RGPD. Toutes les données sont stockées sur des serveurs sécurisés au Sénégal avec des sauvegardes régulières.",
      tags: ["sécurité", "données", "confidentialité"]
    },
    {
      id: 8,
      category: "security",
      question: "Comment activer l'authentification à deux facteurs ?",
      answer: "Accédez à 'Paramètres de sécurité' dans votre profil. Activez l'authentification à deux facteurs et suivez les étapes pour configurer l'application d'authentification (Google Authenticator ou Authy).",
      tags: ["2FA", "sécurité", "authentification"]
    },
    {
      id: 9,
      category: "security",
      question: "Puis-je exporter mes données personnelles ?",
      answer: "Oui, dans la section 'Paramètres de confidentialité', vous pouvez demander l'export de vos données. Un fichier PDF contenant toutes vos informations sera généré sous 48h.",
      tags: ["export", "données", "RGPD"]
    },
    {
      id: 10,
      category: "security",
      question: "Qui a accès à mes informations fiscales ?",
      answer: "Seules les personnes autorisées au sein de l'administration fiscale et vous-même avez accès à vos informations. Nous ne partageons jamais vos données avec des tiers sans votre consentement.",
      tags: ["accès", "confidentialité", "administration"]
    },
    {
      id: 11,
      category: "security",
      question: "Comment signaler une activité suspecte sur mon compte ?",
      answer: "Contactez immédiatement notre équipe de sécurité à security@taxes.gov.sn ou appelez le 33 889 10 30. Indiquez l'heure et le type d'activité suspecte observée.",
      tags: ["signalement", "sécurité", "fraude"]
    },
    
    // Déclarations & Taxes
    {
      id: 12,
      category: "taxes",
      question: "Quand dois-je déclarer mes revenus ?",
      answer: "La déclaration des revenus doit être effectuée avant le 30 avril de chaque année pour les personnes physiques. Les entreprises ont des échéances spécifiques selon leur régime fiscal.",
      tags: ["déclaration", "échéance", "revenus"]
    },
    {
      id: 13,
      category: "taxes",
      question: "Comment déclarer mes revenus en ligne ?",
      answer: "Connectez-vous à votre compte, accédez à 'Mes déclarations' → 'Nouvelle déclaration'. Suivez le formulaire étape par étape et téléchargez les documents justificatifs requis.",
      tags: ["déclaration en ligne", "procédure", "revenus"]
    },
    {
      id: 14,
      category: "taxes",
      question: "Quels documents dois-je préparer pour ma déclaration ?",
      answer: "Préparez : 1. Votre CNI 2. Vos bulletins de salaire 3. Vos relevés bancaires 4. Vos justificatifs de dépenses déductibles 5. Votre dernier avis d'imposition",
      tags: ["documents", "préparation", "déclaration"]
    },
    {
      id: 15,
      category: "taxes",
      question: "Comment corriger une déclaration déjà soumise ?",
      answer: "Vous pouvez effectuer une déclaration rectificative dans les 3 mois suivant la soumission initiale. Accédez à 'Mes déclarations', sélectionnez la déclaration à modifier et cliquez sur 'Rectifier'.",
      tags: ["correction", "rectification", "déclaration"]
    },
    {
      id: 16,
      category: "taxes",
      question: "Qu'est-ce que la taxe sur la valeur ajoutée (TVA) ?",
      answer: "La TVA est un impôt indirect sur la consommation. Le taux normal est de 18%, avec des taux réduits à 10% et 0% pour certains produits et services. Les entreprises doivent la déclarer mensuellement.",
      tags: ["TVA", "explication", "entreprise"]
    },
    {
      id: 17,
      category: "taxes",
      question: "Comment déclarer la TVA en tant qu'entreprise ?",
      answer: "Les entreprises doivent déclarer la TVA mensuellement via le formulaire CA3. Connectez-vous, sélectionnez 'Déclaration TVA', remplissez les montants collectés et déductibles, puis validez.",
      tags: ["TVA", "entreprise", "déclaration"]
    },
    {
      id: 18,
      category: "taxes",
      question: "Quelles sont les exonérations fiscales disponibles ?",
      answer: "Plusieurs exonérations existent : zones franches, investissements prioritaires, entreprises nouvelles, exportateurs. Consultez le Code Général des Impôts ou contactez un conseiller fiscal.",
      tags: ["exonération", "avantages", "fiscalité"]
    },
    
    // Paiements
    {
      id: 19,
      category: "payment",
      question: "Quels moyens de paiement sont acceptés ?",
      answer: "Nous acceptons : virement bancaire, paiement par carte (Visa/Mastercard), paiement mobile (Orange Money, Free Money, Wave), et espèces dans les guichets agréés.",
      tags: ["paiement", "moyens", "options"]
    },
    {
      id: 20,
      category: "payment",
      question: "Comment payer mes impôts en ligne ?",
      answer: "Dans 'Mes paiements', sélectionnez l'impôt à régler. Choisissez votre moyen de paiement préféré et suivez les instructions. Un reçu électronique sera généré après paiement.",
      tags: ["paiement en ligne", "procédure", "impôts"]
    },
    {
      id: 21,
      category: "payment",
      question: "Puis-je payer en plusieurs fois ?",
      answer: "Oui, pour certains impôts, un échéancier de paiement peut être mis en place. Contactez le service recouvrement pour discuter des modalités de paiement échelonné.",
      tags: ["échéancier", "paiement multiple", "flexibilité"]
    },
    {
      id: 22,
      category: "payment",
      question: "Comment obtenir un reçu de paiement ?",
      answer: "Après chaque paiement, un reçu électronique est automatiquement généré. Vous pouvez le télécharger depuis 'Mes documents' ou le recevoir par email si vous avez activé cette option.",
      tags: ["reçu", "justificatif", "paiement"]
    },
    
    // Problèmes techniques
    {
      id: 23,
      category: "technical",
      question: "Le site ne se charge pas, que faire ?",
      answer: "1. Vérifiez votre connexion internet 2. Effacez le cache de votre navigateur 3. Essayez un autre navigateur 4. Contactez le support si le problème persiste",
      tags: ["chargement", "technique", "dépannage"]
    },
    {
      id: 24,
      category: "technical",
      question: "Je ne reçois pas les emails de confirmation",
      answer: "Vérifiez vos courriers indésirables (spam). Ajoutez notifications@taxes.gov.sn à vos contacts. Si le problème persiste, contactez le support pour vérifier votre adresse email.",
      tags: ["email", "confirmation", "communication"]
    },
    {
      id: 25,
      category: "technical",
      question: "L'application mobile ne fonctionne pas",
      answer: "1. Mettez à jour l'application 2. Redémarrez votre téléphone 3. Vérifiez les permissions de l'application 4. Réinstallez l'application si nécessaire",
      tags: ["mobile", "application", "dépannage"]
    }
  ];

  // Filtrer les questions
  const filteredFaqs = faqItems.filter(item => {
    const matchesCategory = activeCategory === "all" || item.category === activeCategory;
    const matchesSearch = searchTerm === "" || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const selectedCategory = categories.find(cat => cat.id === activeCategory);

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">FAQ & Aide</li>
                  </ol>
                  <h4 className="main-title mb-0">
                    <QuestionCircle className="me-2" />
                    Centre d'aide & FAQ
                  </h4>
                  <p className="text-muted mb-0">
                    Trouvez rapidement des réponses à vos questions
                  </p>
                </div>
                
                <div>
                  <Badge bg="info" className="px-3 py-2">
                    <LifePreserver className="me-2" />
                    Support disponible 24/7
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>

          {/* Barre de recherche */}
          <Row className="mb-4">
            <Col md={8} className="mx-auto">
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <h5 className="text-center mb-4">
                    <Book className="me-2 text-primary" />
                    Comment pouvons-nous vous aider ?
                  </h5>
                  <InputGroup className="mb-3">
                    <InputGroup.Text className="bg-light border-end-0">
                      <Search />
                    </InputGroup.Text>
                    <Form.Control
                      type="search"
                      placeholder="Recherchez une question, un mot-clé..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="border-start-0"
                    />
                    <Button variant="primary">
                      Rechercher
                    </Button>
                  </InputGroup>
                  <div className="text-center">
                    <small className="text-muted">
                      Essayez : "mot de passe", "déclaration", "paiement", "TVA"
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Catégories compactes */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex align-items-center mb-3">
                <Filter className="me-2 text-muted" />
                <h6 className="mb-0 me-3">Filtrer par catégorie :</h6>
                <ButtonGroup className="flex-wrap">
                  {categories.map(category => (
                    <Button
                      key={category.id}
                      variant={activeCategory === category.id ? "primary" : "outline-primary"}
                      size="sm"
                      className="me-2 mb-2 d-flex align-items-center"
                      onClick={() => setActiveCategory(category.id)}
                    >
                      <span className="me-1">{category.icon}</span>
                      {category.name}
                      <Badge 
                        bg={activeCategory === category.id ? "light" : "primary"} 
                        text={activeCategory === category.id ? "dark" : "white"}
                        className="ms-2"
                      >
                        {category.count}
                      </Badge>
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            </Col>
          </Row>

          {/* Résultats */}
          <Row className="mb-5">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="mb-0">
                    {selectedCategory?.name} 
                    <Badge bg="light" text="dark" className="ms-2">
                      {filteredFaqs.length} {filteredFaqs.length === 1 ? 'question' : 'questions'}
                    </Badge>
                  </h5>
                  {activeCategory !== "all" && (
                    <small className="text-muted">
                      Catégorie : {categories.find(cat => cat.id === activeCategory)?.name}
                    </small>
                  )}
                </div>
                <div className="text-muted">
                  <ClockHistory className="me-1" />
                  <small>Mis à jour : {new Date().toLocaleDateString('fr-FR')}</small>
                </div>
              </div>

              {filteredFaqs.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                  <Search size={48} className="mb-3 text-info" />
                  <h5>Aucune question trouvée</h5>
                  <p className="mb-3">
                    Essayez avec d'autres mots-clés ou changez de catégorie
                  </p>
                  <Button 
                    variant="outline-info" 
                    onClick={() => {
                      setSearchTerm("");
                      setActiveCategory("all");
                    }}
                  >
                    Voir toutes les questions
                  </Button>
                </Alert>
              ) : (
                <Accordion defaultActiveKey="0" className="faq-accordion">
                  {filteredFaqs.map((item, index) => (
                    <Accordion.Item eventKey={index.toString()} key={item.id} className="mb-3 border">
                      <Accordion.Header className="py-3">
                        <div className="d-flex align-items-center w-100">
                          <div className="flex-grow-1">
                            <h6 className="mb-0">{item.question}</h6>
                            <div className="d-flex gap-2 mt-1">
                              {item.tags.map(tag => (
                                <Badge key={tag} bg="light" text="dark" className="fs-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="py-4">
                        <div className="d-flex">
                          <div className="flex-shrink-0 me-3">
                            <CheckCircle className="text-success mt-1" />
                          </div>
                          <div className="flex-grow-1">
                            <p className="mb-3">{item.answer}</p>
                            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                              <div className="d-flex align-items-center">
                                <Badge bg="secondary" className="me-3">
                                  {categories.find(cat => cat.id === item.category)?.name}
                                </Badge>
                                <small className="text-muted">
                                  Cette réponse vous a-t-elle été utile ?
                                </small>
                                <Button size="sm" variant="outline-success" className="me-2 ms-2">
                                  <CheckCircle className="me-1" /> Oui
                                </Button>
                                <Button size="sm" variant="outline-danger">
                                  <XCircle className="me-1" /> Non
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Col>
          </Row>

          {/* Support supplémentaire */}
     
        </Container>

        <Footer />
      </div>

      <style>{`
        .faq-accordion .accordion-button {
          background-color: #f8f9fa;
          font-weight: 500;
        }
        
        .faq-accordion .accordion-button:not(.collapsed) {
          background-color: #e7f1ff;
          color: #0d6efd;
        }
        
        .faq-accordion .accordion-button:focus {
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .fs-xs {
          font-size: 0.75rem;
        }
        
        .btn-group.flex-wrap {
          gap: 0.25rem;
        }
        
        @media (max-width: 768px) {
          .btn-group.flex-wrap .btn {
            font-size: 0.8rem;
            padding: 0.25rem 0.5rem;
          }
          
          .btn-group.flex-wrap .badge {
            font-size: 0.7rem;
            padding: 0.1rem 0.3rem;
          }
        }
      `}</style>
    </React.Fragment>
  );
}